import { useEffect, useId, useState } from "react";
import { useBrand } from "@/lib/brand";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Copy, Check } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { esAdmin } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { eur } from "@/lib/format";
import { METODO_COBRO } from "@/lib/registro";

export default function NewReservation() {
  const brand = useBrand();
  const navigate = useNavigate();
  const uid = useId();
  // Flota es del administrador (App.js, SoloAdmin): al operador no se le ofrece.
  const admin = esAdmin(useAuth()?.user);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    vehicle_id: "",
    client_name: "",
    client_email: "",
    client_phone: "",
    pickup_date: "",
    pickup_time: "10:00",
    return_date: "",
    return_time: "18:00",
    pickup_location: "",
    return_location: "",
    deposit_amount: 0,
    total_amount: 0,
    km_included_per_day: 200,
    extra_km_rate: 0.3,
    notes: "",
    rental_charged_externally: false,
    // Cómo se cobró fuera (registro de viajeros: el pago que se comunica). Solo cuenta si se cobró fuera.
    rental_payment_method: "",
    // Alquiler en el momento, sin reserva previa: al registro solo se le comunica la entrega.
    walk_in: false,
    locale: "es",
    self_service: false,
    // Señal al reservar (% del alquiler). Se rellena con el ajuste global y
    // queda congelado en la reserva al crearla.
    signal_percent: 30,
  });

  // Con el registro de viajeros activado, el alta pide lo suyo (forma de cobro externa y «sin reserva previa»); sin
  // él, o sin poder leer sus ajustes, nada.
  const [registro, setRegistro] = useState(false);
  useEffect(() => {
    api.get("/admin/registry/settings")
      .then(({ data }) => setRegistro(data?.enabled === true))
      .catch(() => setRegistro(false));
  }, []);

  useEffect(() => {
    api.get("/admin/payment-settings")
      .then(({ data }) => setForm((f) => ({ ...f, signal_percent: Number(data.signal_percent) })))
      .catch(() => {});
  }, []);

  const [vehicles, setVehicles] = useState([]);
  const [freeIds, setFreeIds] = useState(null);
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    api.get("/fleet/vehicles")
      .then(({ data }) => setVehicles((data.vehicles || []).filter((v) => v.active)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const { pickup_date: start, return_date: end, vehicle_id } = form;
    if (!start || !end || end < start) { setFreeIds(null); setQuote(null); return; }
    api.get("/fleet/availability", { params: { start, end } })
      .then(({ data }) => setFreeIds(new Set(data.vehicles.filter((v) => v.free).map((v) => v.id))))
      .catch(() => setFreeIds(null));
    if (!vehicle_id) { setQuote(null); return; }
    api.get("/fleet/quote", { params: { vehicle_id, start, end } })
      .then(({ data }) => setQuote(data))
      .catch(() => setQuote(null));
  }, [form.pickup_date, form.return_date, form.vehicle_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const chooseVehicle = (e) => {
    const v = vehicles.find((x) => x.id === e.target.value);
    setForm((f) => ({
      ...f,
      vehicle_id: e.target.value,
      ...(v ? { deposit_amount: v.deposit_amount, km_included_per_day: v.km_included_per_day, extra_km_rate: v.extra_km_rate } : {}),
    }));
  };

  const update = (k) => (e) => {
    const v =
      e?.target?.type === "number" ? Number(e.target.value) : e?.target?.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.return_date < form.pickup_date) {
      toast.error("La fecha de devolución no puede ser anterior a la de recogida");
      return;
    }
    if (form.return_date === form.pickup_date && form.return_time <= form.pickup_time) {
      toast.error("Si la devolución es el mismo día, la hora debe ser posterior a la de recogida");
      return;
    }
    if (!form.vehicle_id) {
      toast.error("Elige el vehículo de la reserva");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/reservations", {
        ...form,
        walk_in: registro && form.walk_in,
        rental_payment_method: registro && form.rental_charged_externally && form.rental_payment_method
          ? form.rental_payment_method : null,
      });
      setCreated(data);
      toast.success("Reserva creada");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const link = created
    ? `${window.location.origin}/reserva/${created.code}`
    : "";

  const copyLink = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (created) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]">
        <div className="container-x py-12 max-w-2xl">
          <div className="bg-white border border-[#DCD8D0] rounded-xl p-8 fade-in">
            <div className="h-12 w-12 rounded-full bg-[#4A7C59]/15 text-[#4A7C59] flex items-center justify-center mb-4">
              <Check className="h-6 w-6" />
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A] mb-2">
              Reserva creada
            </h1>
            <p className="text-[#5C5C5C] mb-6">
              Comparte este código o enlace con {created.client_name} para que
              pueda hacer su check-in.
            </p>

            <div className="bg-[#F7F5F0] border border-[#DCD8D0] rounded-xl p-5 mb-3 text-center">
              <div className="text-overline mb-2">Código</div>
              <div className="font-mono text-3xl sm:text-4xl font-bold tracking-widest text-marca" data-testid="created-code">
                {created.code}
              </div>
            </div>

            <button
              onClick={copyLink}
              className="w-full bg-marca hover:bg-marca-hover text-white rounded-lg px-4 py-3 flex items-center justify-between gap-3 transition-colors"
              data-testid="created-copy-link"
            >
              <span className="text-sm truncate">{link}</span>
              {copied ? (
                <Check className="h-4 w-4 shrink-0" />
              ) : (
                <Copy className="h-4 w-4 shrink-0" />
              )}
            </button>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setCreated(null);
                  setForm((f) => ({ ...f, client_name: "", client_email: "", client_phone: "", notes: "" }));
                }}
                className="border-[#DCD8D0]"
                data-testid="created-new-btn"
              >
                Crear otra
              </Button>
              <Button
                onClick={() => navigate(`/admin/reservations/${created.id}`)}
                className="bg-marca hover:bg-marca-hover text-white"
                data-testid="created-view-btn"
              >
                Ver detalle
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <header className="bg-white border-b border-[#DCD8D0]">
        <div className="container-x py-4 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center text-[#5C5C5C] hover:text-marca" data-testid="new-back-link">
            <ArrowLeft className="h-4 w-4 mr-2" /> Panel
          </Link>
        </div>
      </header>

      <main className="container-x py-8 max-w-3xl">
        <span className="text-overline">Nueva reserva</span>
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A] mt-2 mb-8">
          Crear reserva y generar código
        </h1>

        <form onSubmit={submit} className="space-y-8" data-testid="new-reservation-form">
          <section className="bg-white border border-[#DCD8D0] rounded-xl p-6">
            <h2 className="font-display text-lg font-semibold text-[#1A1A1A] mb-4">Vehículo</h2>
            {vehicles.length === 0 ? (
              <p className="text-sm text-[#5C5C5C]">
                {admin ? (
                  <>No hay vehículos activos. <Link to="/admin/flota" className="underline">Crea uno en Flota</Link>.</>
                ) : "No hay vehículos activos. Pide a un administrador que active uno en Flota."}
              </p>
            ) : (
              <select
                value={form.vehicle_id}
                onChange={chooseVehicle}
                className="h-10 w-full rounded-md border border-[#DCD8D0] bg-[#F7F5F0] px-3 text-sm"
                data-testid="new-vehicle"
              >
                <option value="">Elige un vehículo…</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id} disabled={freeIds ? !freeIds.has(v.id) : false}>
                    {v.name} · {v.plate}{freeIds && !freeIds.has(v.id) ? " — ocupado esas fechas" : ""}
                  </option>
                ))}
              </select>
            )}
            {quote && (
              <div className="mt-3 text-sm" data-testid="new-quote">
                {quote.ok ? (
                  <span className="text-[#1A1A1A]">
                    Precio según tarifas: <strong>{eur(quote.base_total)}</strong> ({quote.nights} {quote.nights === 1 ? "noche" : "noches"}){" "}
                    <button type="button" className="underline text-marca ml-2"
                      onClick={() => setForm((f) => ({ ...f, total_amount: quote.base_total }))} data-testid="new-quote-use">
                      Usar este importe
                    </button>
                  </span>
                ) : (
                  <span className="text-acento">{quote.error}</span>
                )}
              </div>
            )}
          </section>
          <section className="bg-white border border-[#DCD8D0] rounded-xl p-6">
            <h2 className="font-display text-lg font-semibold text-[#1A1A1A] mb-4">Cliente</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Nombre completo</Label>
                <Input required value={form.client_name} onChange={update("client_name")} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="new-client-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" required value={form.client_email} onChange={update("client_email")} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="new-client-email" />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input required value={form.client_phone} onChange={update("client_phone")} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="new-client-phone" />
              </div>
              <div>
                <Label>Idioma de los avisos</Label>
                <select
                  value={form.locale}
                  onChange={update("locale")}
                  className="mt-2 h-10 w-full rounded-md border border-[#DCD8D0] bg-[#F7F5F0] px-3 text-sm"
                  data-testid="new-locale"
                >
                  <option value="es">Castellano</option>
                  <option value="ca">Català</option>
                </select>
              </div>
              <p className="sm:col-span-2 text-xs text-[#8A8A8A]" data-testid="new-whatsapp-info">
                Los avisos por WhatsApp los acepta el propio cliente en el pre-checkin.
              </p>
            </div>
          </section>

          <section className="bg-white border border-[#DCD8D0] rounded-xl p-6">
            <h2 className="font-display text-lg font-semibold text-[#1A1A1A] mb-4">Fechas</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Fecha recogida</Label>
                <Input type="date" required value={form.pickup_date} onChange={update("pickup_date")} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="new-pickup-date" />
              </div>
              <div>
                <Label>Hora recogida</Label>
                <Input type="time" required value={form.pickup_time} onChange={update("pickup_time")} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="new-pickup-time" />
              </div>
              <div>
                <Label>Fecha devolución</Label>
                <Input type="date" required min={form.pickup_date || undefined} value={form.return_date} onChange={update("return_date")} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="new-return-date" />
              </div>
              <div>
                <Label>Hora devolución</Label>
                <Input type="time" required value={form.return_time} onChange={update("return_time")} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="new-return-time" />
              </div>
              <div>
                <Label>Lugar recogida</Label>
                <Input required value={form.pickup_location} onChange={update("pickup_location")} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" />
              </div>
              <div>
                <Label>Lugar devolución</Label>
                <Input required value={form.return_location} onChange={update("return_location")} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" />
              </div>
            </div>
          </section>

          <section className="bg-white border border-[#DCD8D0] rounded-xl p-6">
            <h2 className="font-display text-lg font-semibold text-[#1A1A1A] mb-4">Tarifas</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Fianza (€)</Label>
                <Input type="number" min="0" step="10" value={form.deposit_amount} onChange={update("deposit_amount")} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="new-deposit" />
              </div>
              <div>
                <Label>Importe total (€)</Label>
                <Input type="number" min="0" step="1" value={form.total_amount} onChange={update("total_amount")} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="new-total-amount" />
              </div>
              <div>
                <Label>Señal al reservar (%)</Label>
                <Input type="number" min="1" max="100" step="1" value={form.signal_percent} onChange={update("signal_percent")} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" data-testid="new-signal-percent" />
                <div className="text-xs text-[#8A8A8A] mt-1">
                  {Number(form.total_amount) > 0
                    ? `${eur((Number(form.total_amount) * Number(form.signal_percent || 0)) / 100)} ahora · resto antes de la entrega`
                    : "Porcentaje del importe total que paga ahora el cliente"}
                </div>
              </div>
              <div>
                <Label>Km incluidos / día</Label>
                <Input type="number" min="0" value={form.km_included_per_day} onChange={update("km_included_per_day")} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" />
              </div>
              <div>
                <Label>Tarifa km extra (€)</Label>
                <Input type="number" min="0" step="0.01" value={form.extra_km_rate} onChange={update("extra_km_rate")} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" />
              </div>
              <div className="sm:col-span-2">
                <label className="flex items-start gap-3 cursor-pointer mt-1">
                  <Checkbox
                    checked={form.rental_charged_externally}
                    onCheckedChange={(v) =>
                      setForm((f) => ({ ...f, rental_charged_externally: Boolean(v) }))
                    }
                    data-testid="new-rental-external"
                  />
                  <span className="text-sm text-[#5C5C5C]">
                    El alquiler ya está cobrado fuera de la plataforma (p. ej.
                    en otro portal de alquiler). Si no lo marcas, el cliente paga ahora la señal y,
                    unos días antes de la entrega, recibe un correo para pagar el
                    resto junto con la fianza. Si la recogida es inminente
                    (devolución en menos de una semana), lo paga todo junto en el
                    pre-checkin, o podrás cobrarlo tú con la tarjeta guardada.
                  </span>
                </label>
              </div>
              {registro && form.rental_charged_externally && (
                <div className="sm:col-span-2">
                  <Label htmlFor={`${uid}-rental-method`}>¿Cómo se cobró? (para el registro de viajeros)</Label>
                  <select id={`${uid}-rental-method`} value={form.rental_payment_method}
                    onChange={(e) => setForm((f) => ({ ...f, rental_payment_method: e.target.value }))}
                    className="mt-2 h-10 w-full rounded-md border border-[#DCD8D0] bg-[#F7F5F0] px-3 text-sm" data-testid="new-rental-method">
                    <option value="">Sin indicar</option>
                    {Object.entries(METODO_COBRO).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                  </select>
                </div>
              )}
              {registro && (
                <div className="sm:col-span-2 flex items-start gap-3 mt-1">
                  <Checkbox id={`${uid}-walk-in`} checked={form.walk_in}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, walk_in: Boolean(v) }))} data-testid="new-walk-in" />
                  <Label htmlFor={`${uid}-walk-in`} className="text-sm font-normal leading-snug text-[#5C5C5C] cursor-pointer">
                    Alquiler en el momento, sin reserva previa: al registro de viajeros solo se le comunica la entrega.
                  </Label>
                </div>
              )}
            </div>
          </section>

          <section className="bg-white border border-[#DCD8D0] rounded-xl p-6">
            <h2 className="font-display text-lg font-semibold text-[#1A1A1A] mb-4">Entrega</h2>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={form.self_service}
                onCheckedChange={(v) => setForm((f) => ({ ...f, self_service: Boolean(v) }))}
                data-testid="new-self-service"
              />
              <span className="text-sm text-[#5C5C5C]">
                <strong className="text-[#1A1A1A]">Recogida y devolución autónomas.</strong>{" "}
                El cliente hace la entrega desde su móvil junto a la furgoneta
                (verificaciones + fotos + GPS) y tú solo abres/cierras a
                distancia con {brand?.remote_app_name || "la app del vehículo"} cuando te llegue el aviso.
                Recuerda dejar la llave en su funda de la guantera.
              </span>
            </label>
          </section>

          <section className="bg-white border border-[#DCD8D0] rounded-xl p-6">
            <Label>Notas internas</Label>
            <Textarea value={form.notes} onChange={update("notes")} className="mt-2 bg-[#F7F5F0] border-[#DCD8D0]" rows={3} />
          </section>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate("/admin")} className="border-[#DCD8D0]">
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} className="bg-marca hover:bg-marca-hover text-white px-8" data-testid="new-submit-btn">
              {loading ? "Creando..." : "Crear reserva y generar código"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
